import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  increment,
  limit,
  setDoc,
  arrayUnion,
  arrayRemove,
  getAggregateFromServer,
  sum,
  count,
  deleteField
} from "./fs";
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import { auth, db, storage } from "../config/firebase";
import { isDemoMode as isDemoSession, isDemoEmail } from "./demoMode";
import { listOwned, listOwnedPage, currentUid, ownerField, sortRows } from "./ownership";
import { defaultWorkspaceId, getTenancy, licenseCodeOf, scopeWhere, tenantOf } from "./tenancy";
import { GroupUsageService, assertUserCapacity, countGroupUsers, getGroupCapacity, groupCapacity } from "./groupQuota";
import { logError } from "./logger";
import { GB, QuotaError, crossedWarning, evaluateQuota, readUsage } from "./quota";
import { moveFolderError } from "./folderTree";
import { isOnLegalHold, isPurgeDue, isTrashed, withoutTrashed } from "./trashPolicy";
import { validateInvoiceKind } from "./invoiceKinds";

function isDemoMode(user) {
  if (isDemoSession(user)) return true;
  if (isDemoSession()) return true;
  if (!user) return false;
  if (typeof user === "string") return user === "guest" || user === "demo-user";
  return isDemoEmail(user.email) || user.uid === "guest" || user.uid === "demo-user";
}




export const FolderService = {
  async getFolders(userId) {
    const q = query(collection(db, "folders"), scopeWhere("folders", "userId", userId, currentUid()), orderBy("createdAt", "desc"));
    const snap = await getDocs(q);
    return withoutTrashed(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  },

  subscribeFolders(userId, callback) {
    const q = query(collection(db, "folders"), scopeWhere("folders", "userId", userId, currentUid()), orderBy("createdAt", "desc"));
    return onSnapshot(q, (snap) => {
      callback(withoutTrashed(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
    });
  },

  async createFolder(userId, name, color = "#5e72e4", parentId = null) {
    // Mobil kök klasörleri `parentId == null` ile listeler; alan her zaman yazılır.
    const ref_ = await addDoc(collection(db, "folders"), {
      name, color, userId, parentId: parentId || null, fileCount: 0, createdAt: serverTimestamp()
    });
    return { id: ref_.id, name, color, userId, parentId: parentId || null, fileCount: 0, createdAt: new Date() };
  },

  async renameFolder(folderId, newName) {
    await updateDoc(doc(db, "folders", folderId), { name: newName });
  },

  /** Rejects moves into itself, into a descendant, or past MAX_FOLDER_DEPTH. */
  async moveFolder(folderId, newParentId = null) {
    const snap = await getDoc(doc(db, "folders", folderId));
    if (!snap.exists()) throw Object.assign(new Error("FOLDER_NOT_FOUND"), { code: "FOLDER_NOT_FOUND" });
    const folder = snap.data();
    const target = newParentId || null;
    if ((folder.parentId || null) === target) return;
    const folders = await this.getFolders(folder.userId);
    const code = moveFolderError(folders, folderId, target);
    if (code) throw Object.assign(new Error(code), { code });
    await updateDoc(doc(db, "folders", folderId), { parentId: target, updatedAt: serverTimestamp() });
    await recordAdminAction("FOLDER_MOVE", {
      entityType: "folder",
      entityId: folderId,
      previousValue: { parentId: folder.parentId || null },
      newValue: { parentId: target }
    });
  },

  /**
   * Moves the folder and the files directly in it to the trash; subfolders move
   * up to the folder's parent so they are not orphaned.
   */
  async deleteFolder(folderId) {
    const snap = await getDoc(doc(db, "folders", folderId));
    if (!snap.exists()) return;
    const folder = snap.data();
    const parentId = folder.parentId || null;
    const [childFolders, childFiles] = await Promise.all([
    getDocs(query(collection(db, "folders"), scopeWhere("folders", "userId", folder.userId, currentUid()), where("parentId", "==", folderId))),
    getDocs(query(collection(db, "files"), scopeWhere("files", "userId", folder.userId, currentUid()), where("folderId", "==", folderId)))]
    );
    if (childFiles.docs.some((d) => isOnLegalHold(d.data()))) throw Object.assign(new Error("FOLDER_HAS_LEGAL_HOLD"), { code: "FOLDER_HAS_LEGAL_HOLD" });
    for (const d of childFolders.docs) {
      if (!isTrashed(d.data())) await updateDoc(doc(db, "folders", d.id), { parentId, updatedAt: serverTimestamp() });
    }
    for (const d of childFiles.docs) {
      if (!isTrashed(d.data())) await updateDoc(doc(db, "files", d.id), trashFields(d.data(), folderId));
    }
    await updateDoc(doc(db, "folders", folderId), {
      deletedAt: serverTimestamp(),
      deletedBy: currentUid(),
      updatedAt: serverTimestamp()
    });
    await recordAdminAction("FOLDER_TRASH", {
      entityType: "folder",
      entityId: folderId,
      previousValue: { name: folder.name || null, parentId },
      meta: { files: childFiles.docs.length }
    });
  }
};

function trashFields(file, trashedWithFolder = null) {
  return {
    status: "DELETED",
    statusBeforeDelete: file?.status && file.status !== "DELETED" ? file.status : "READY",
    deletedAt: serverTimestamp(),
    deletedBy: currentUid(),
    trashedWithFolder,
    updatedAt: serverTimestamp()
  };
}

function restoreFields(file, folderId) {
  return {
    status: file?.statusBeforeDelete || "READY",
    statusBeforeDelete: deleteField(),
    deletedAt: deleteField(),
    deletedBy: deleteField(),
    trashedWithFolder: deleteField(),
    folderId,
    updatedAt: serverTimestamp()
  };
}

async function liveFolderId(folderId) {
  if (!folderId) return null;
  const snap = await getDoc(doc(db, "folders", folderId)).catch(() => null);
  return snap?.exists() && !isTrashed(snap.data()) ? folderId : null;
}

export async function removeStorageObjects(file) {
  const paths = new Set([file?.storagePath].filter(Boolean));
  const versions = await getDocs(collection(db, "files", file.id, "versions")).catch(() => null);
  for (const v of versions?.docs || []) {
    if (v.data().storagePath) paths.add(v.data().storagePath);
    await deleteDoc(v.ref).catch(() => {});
  }
  for (const path of paths) {
    try {await deleteObject(ref(storage, path));} catch (e) {}
  }
  return paths.size;
}




export const FileService = {
  async getFiles(userId, folderId = null) {
    let q;
    if (folderId) {
      q = query(collection(db, "files"), scopeWhere("files", "userId", userId, currentUid()), where("folderId", "==", folderId), orderBy("createdAt", "desc"));
    } else {
      q = query(collection(db, "files"), scopeWhere("files", "userId", userId, currentUid()), orderBy("createdAt", "desc"));
    }
    const snap = await getDocs(q);
    return withoutTrashed(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  },

  /** Files that are not inside any folder (missing or null `folderId`). */
  async getRootFiles(userId) {
    const all = await this.getFiles(userId, null);
    return all.filter((f) => !f.folderId);
  },

  async moveFile(fileId, targetFolderId = null) {
    const snap = await getDoc(doc(db, "files", fileId));
    if (!snap.exists()) throw Object.assign(new Error("FILE_NOT_FOUND"), { code: "FILE_NOT_FOUND" });
    const from = snap.data().folderId || null;
    const to = targetFolderId || null;
    if (from === to) return;
    if (to) {
      const target = await getDoc(doc(db, "folders", to));
      if (!target.exists()) throw Object.assign(new Error("FOLDER_NOT_FOUND"), { code: "FOLDER_NOT_FOUND" });
    }
    await updateDoc(doc(db, "files", fileId), { folderId: to, updatedAt: serverTimestamp() });
    if (from) await updateDoc(doc(db, "folders", from), { fileCount: increment(-1) }).catch(() => {});
    if (to) await updateDoc(doc(db, "folders", to), { fileCount: increment(1) });
    await recordAdminAction("FILE_MOVE", {
      entityType: "file",
      entityId: fileId,
      previousValue: { folderId: from },
      newValue: { folderId: to }
    });
  },

  async getFileById(fileId) {
    const snap = await getDoc(doc(db, "files", fileId));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  },

  subscribeFiles(userId, callback, folderId = null) {
    let q;
    if (folderId) {
      q = query(collection(db, "files"), scopeWhere("files", "userId", userId, currentUid()), where("folderId", "==", folderId), orderBy("createdAt", "desc"));
    } else {
      q = query(collection(db, "files"), scopeWhere("files", "userId", userId, currentUid()), orderBy("createdAt", "desc"));
    }
    return onSnapshot(q, (snap) => {
      callback(withoutTrashed(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
    });
  },

  async uploadFile(userId, file, folderId, tags = [], onProgress, { process = true } = {}) {
    await UsageService.assertCanUpload(userId, file.size || 0);
    const filePath = `files/${userId}/${Date.now()}_${file.name}`;
    const storageRef = ref(storage, filePath);
    const uploadTask = uploadBytesResumable(storageRef, file);

    return new Promise((resolve, reject) => {
      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress = snapshot.bytesTransferred / snapshot.totalBytes * 100;
          if (onProgress) onProgress(progress);
        },
        (error) => reject(error),
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            const docRef = await addDoc(collection(db, "files"), {
              name: file.name,
              size: file.size,
              mimeType: file.type,
              downloadURL,
              storagePath: filePath,
              userId,
              folderId: folderId || null,
              tags,
              version: 1,
              status: process ? "PROCESSING" : "READY",
              ocrConfidence: null,
              ocrText: null,
              classification: null,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            });

            await addDoc(collection(db, "files", docRef.id, "versions"), {
              versionNum: 1,
              downloadURL,
              storagePath: filePath,
              size: file.size,
              changedBy: userId,
              displayName: auth?.currentUser?.displayName || auth?.currentUser?.email || "",
              changeNote: "Initial upload",
              createdAt: serverTimestamp()
            });

            if (folderId) {
              await updateDoc(doc(db, "folders", folderId), { fileCount: increment(1) });
            }

            await UsageService.recordUpload(userId, file.size || 0);
            resolve({ id: docRef.id, name: file.name, downloadURL });
          } catch (err) {
            reject(err);
          }
        }
      );
    });
  },

  /** Moves the file to the trash; it can be restored for TRASH_RETENTION_DAYS. */
  async deleteFile(fileId) {
    const snap = await getDoc(doc(db, "files", fileId));
    if (!snap.exists()) return;
    const file = snap.data();
    if (isTrashed(file)) return;
    if (isOnLegalHold(file)) throw Object.assign(new Error("LEGAL_HOLD"), { code: "LEGAL_HOLD" });
    await updateDoc(doc(db, "files", fileId), trashFields(file));
    if (file.folderId) {
      await updateDoc(doc(db, "folders", file.folderId), { fileCount: increment(-1) }).catch(() => {});
    }
    await recordAdminAction("FILE_TRASH", {
      entityType: "file",
      entityId: fileId,
      previousValue: { name: file.name || null, userId: file.userId || null, folderId: file.folderId || null }
    });
  },

  async updateFile(fileId, data) {
    await updateDoc(doc(db, "files", fileId), { ...data, updatedAt: serverTimestamp() });
  },

  async updateOcrText(fileId, ocrText) {
    await updateDoc(doc(db, "files", fileId), { ocrText, updatedAt: serverTimestamp() });
  },

  async approveOcr(fileId) {
    await updateDoc(doc(db, "files", fileId), { status: "READY", ocrApproved: true, updatedAt: serverTimestamp() });
  },

  async getVersionHistory(fileId) {
    const q = query(collection(db, "files", fileId, "versions"), orderBy("createdAt", "desc"));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  async restoreVersion(fileId, versionId) {
    const vDoc = await getDoc(doc(db, "files", fileId, "versions", versionId));
    if (!vDoc.exists()) throw new Error("Version not found");
    const vData = vDoc.data();

    const fileDoc = await getDoc(doc(db, "files", fileId));
    if (fileDoc.exists()) {
      const current = fileDoc.data();
      const versionsSnap = await getDocs(query(collection(db, "files", fileId, "versions"), orderBy("createdAt", "desc")));
      const nextNum = versionsSnap.docs.length + 1;
      await addDoc(collection(db, "files", fileId, "versions"), {
        versionNum: nextNum,
        downloadURL: current.downloadURL || "",
        size: current.size || 0,
        changedBy: current.updatedBy || "system",
        displayName: "System (auto-save before restore)",
        changeNote: `Auto-saved before restoring v${vData.versionNum || versionId}`,
        createdAt: serverTimestamp()
      });
    }
    await updateDoc(doc(db, "files", fileId), {
      downloadURL: vData.downloadURL,
      size: vData.size,
      updatedAt: serverTimestamp()
    });
  },

  async uploadNewVersion(fileId, file, changeNote, userId) {
    const { ref: storageRef, uploadBytes, getDownloadURL: getURL } = await import("firebase/storage");
    const { storage } = await import("../config/firebase");
    const fileRef = storageRef(storage, `files/${fileId}/versions/${Date.now()}_${file.name}`);
    await uploadBytes(fileRef, file);
    const downloadURL = await getURL(fileRef);
    const versionsSnap = await getDocs(query(collection(db, "files", fileId, "versions"), orderBy("createdAt", "desc")));
    const nextNum = versionsSnap.docs.length + 1;
    const verRef = await addDoc(collection(db, "files", fileId, "versions"), {
      versionNum: nextNum,
      downloadURL,
      size: file.size,
      changedBy: userId,
      changeNote,
      createdAt: serverTimestamp()
    });
    await updateDoc(doc(db, "files", fileId), {
      downloadURL,
      size: file.size,
      updatedAt: serverTimestamp(),
      updatedBy: userId
    });
    return { id: verRef.id };
  },

  async searchFiles(userId, searchQuery, filters = {}) {

    const all = await this.getFiles(userId);
    const q = (searchQuery || "").toLowerCase();
    let results = all;
    if (q) {
      results = results.filter(
        (f) =>
        (f.name || "").toLowerCase().includes(q) ||
        (f.tags || []).some((t) => t.toLowerCase().includes(q)) ||
        (f.ocrText || "").toLowerCase().includes(q)
      );
    }
    if (filters.fileType && filters.fileType !== "all") {
      const typeMap = { pdf: "pdf", doc: "word", image: "image", spreadsheet: "sheet" };
      const keyword = typeMap[filters.fileType];
      if (keyword) results = results.filter((f) => (f.mimeType || "").includes(keyword));
    }
    if (filters.status && filters.status !== "all") {
      results = results.filter((f) => f.status === filters.status);
    }
    return results;
  }
};




/**
 * Tenant anchor for role shares: a user manager shares with its own sub-users,
 * everyone else with colleagues under the same parent account. Rules accept
 * only these two values.
 */
export function roleShareScope(profile, uid) {
  const role = profile?.role || "";
  if (["admin", "company", "dealer", "management"].includes(role)) return uid || null;
  return profile?.parentUid || null;
}

/**
 * The signed-in user's trash (plan 4.4). Lists use the owner filter so they
 * work with and without the workspace scope; permanent deletes are only
 * allowed on trashed items (firestore.rules).
 */
export const TrashService = {
  async list(uid = currentUid()) {
    if (!uid) return { files: [], folders: [] };
    const [files, folders] = await Promise.all([
    getDocs(query(collection(db, "files"), where("userId", "==", uid), where("status", "==", "DELETED"))),
    getDocs(query(collection(db, "folders"), where("userId", "==", uid)))]
    );
    return {
      files: sortRows(files.docs.map((d) => ({ id: d.id, ...d.data() })), "deletedAt"),
      folders: sortRows(folders.docs.map((d) => ({ id: d.id, ...d.data() })).filter(isTrashed), "deletedAt")
    };
  },

  async restoreFile(fileId) {
    const snap = await getDoc(doc(db, "files", fileId));
    if (!snap.exists() || !isTrashed(snap.data())) return;
    const file = snap.data();
    const folderId = await liveFolderId(file.folderId);
    await updateDoc(doc(db, "files", fileId), restoreFields(file, folderId));
    if (folderId) await updateDoc(doc(db, "folders", folderId), { fileCount: increment(1) }).catch(() => {});
    await recordAdminAction("FILE_RESTORE", { entityType: "file", entityId: fileId, newValue: { folderId } });
  },

  /** Restores the folder and the files that were trashed together with it. */
  async restoreFolder(folderId) {
    const snap = await getDoc(doc(db, "folders", folderId));
    if (!snap.exists() || !isTrashed(snap.data())) return;
    const folder = snap.data();
    const parentId = await liveFolderId(folder.parentId);
    const files = await getDocs(query(collection(db, "files"),
    where("userId", "==", folder.userId), where("trashedWithFolder", "==", folderId)));
    await updateDoc(doc(db, "folders", folderId), {
      deletedAt: deleteField(),
      deletedBy: deleteField(),
      parentId,
      fileCount: files.docs.length,
      updatedAt: serverTimestamp()
    });
    for (const d of files.docs) {
      await updateDoc(d.ref, restoreFields(d.data(), folderId));
    }
    await recordAdminAction("FOLDER_RESTORE", { entityType: "folder", entityId: folderId, meta: { files: files.docs.length } });
  },

  async purgeFile(fileId) {
    const snap = await getDoc(doc(db, "files", fileId));
    if (!snap.exists() || !isTrashed(snap.data()) || isOnLegalHold(snap.data())) return;
    const file = { id: fileId, ...snap.data() };
    await removeStorageObjects(file);
    await deleteDoc(doc(db, "files", fileId));
    await recordAdminAction("FILE_DELETE", {
      entityType: "file",
      entityId: fileId,
      previousValue: { name: file.name || null, userId: file.userId || null, folderId: file.folderId || null },
      meta: { storagePath: file.storagePath || null }
    });
  },

  async purgeFolder(folderId) {
    const snap = await getDoc(doc(db, "folders", folderId));
    if (!snap.exists() || !isTrashed(snap.data())) return;
    const folder = snap.data();
    const files = await getDocs(query(collection(db, "files"),
    where("userId", "==", folder.userId), where("trashedWithFolder", "==", folderId)));
    for (const d of files.docs) await this.purgeFile(d.id);
    await deleteDoc(doc(db, "folders", folderId));
    await recordAdminAction("FOLDER_DELETE", { entityType: "folder", entityId: folderId, previousValue: { name: folder.name || null } });
  },

  /** Permanently deletes everything in the trash, or only expired items. */
  async empty({ expiredOnly = false, now = Date.now() } = {}) {
    const { files, folders } = await this.list();
    const due = (item) => !expiredOnly || isPurgeDue(item, now);
    let purged = 0;
    for (const folder of folders.filter(due)) {
      await this.purgeFolder(folder.id);
      purged += 1;
    }
    const purgedFolders = new Set(folders.filter(due).map((f) => f.id));
    for (const file of files.filter((f) => due(f) && !purgedFolders.has(f.trashedWithFolder))) {
      await this.purgeFile(file.id);
      purged += 1;
    }
    return purged;
  }
};

export const ShareService = {
  /** `roles: []` removes the role share. */
  async setRoleShare(fileId, { roles = [], permission = "view" } = {}, profile, uid) {
    const clean = [...new Set((roles || []).filter(Boolean))];
    const scope = roleShareScope(profile, uid);
    if (clean.length && !scope) throw Object.assign(new Error("NO_ROLE_SCOPE"), { code: "NO_ROLE_SCOPE" });
    const before = await getDoc(doc(db, "files", fileId));
    const prev = before.exists() ? before.data() : {};
    const data = clean.length ?
    { sharedWithRoles: clean, sharedRolesScope: scope, sharedRolesPermission: permission === "edit" ? "edit" : "view", isShared: true } :
    { sharedWithRoles: [], sharedRolesScope: null, sharedRolesPermission: "view" };
    await updateDoc(doc(db, "files", fileId), data);
    await recordAdminAction(clean.length ? "FILE_SHARE_ROLES" : "FILE_SHARE_ROLES_REVOKE", {
      entityType: "file",
      entityId: fileId,
      previousValue: { roles: prev.sharedWithRoles || [], permission: prev.sharedRolesPermission || null },
      newValue: { roles: clean, permission: data.sharedRolesPermission, scope: data.sharedRolesScope }
    });
    return data;
  },

  /** Files shared with the caller's role inside their company. */
  async getRoleSharedFiles(profile) {
    const scope = profile?.parentUid;
    if (!scope || !profile?.role) return [];
    const snap = await getDocs(query(collection(db, "files"),
    where("sharedRolesScope", "==", scope), where("sharedWithRoles", "array-contains", profile.role)));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((f) => f.status !== "DELETED");
  },

  async getShares(fileId) {
    return listOwned("shares", { filters: [["fileId", fileId]], includeDeleted: true });
  },

  /** Shares of the file addressed to the signed-in recipient. */
  async getMySharesFor(fileId, email) {
    const recipient = String(email || "").trim().toLowerCase();
    if (!recipient) return [];
    const snap = await getDocs(query(collection(db, "shares"), where("fileId", "==", fileId), where("sharedWith", "==", recipient)));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  async logShareView(fileId, { watermark, shareId } = {}) {
    await recordAdminAction("FILE_SHARE_VIEW", { entityType: "file", entityId: fileId, meta: { watermark: !!watermark, shareId: shareId || null } });
  },

  /** Time-limited shares are always watermarked for the recipient (3.14); `watermark` adds it to open-ended ones. */
  async shareFile(fileId, userId, email, permission = "view", expiryDays = null, { watermark = false } = {}) {
    const recipient = String(email || "").trim().toLowerCase();
    const data = {
      fileId, sharedBy: userId, sharedWith: recipient, permission, isActive: true, createdAt: serverTimestamp(),
      watermark: !!(watermark || expiryDays)
    };
    if (expiryDays) {
      const exp = new Date();
      exp.setDate(exp.getDate() + parseInt(expiryDays));
      data.expiresAt = exp;
    }
    const ref_ = await addDoc(collection(db, "shares"), data);
    await updateDoc(doc(db, "files", fileId), {
      isShared: true,
      sharedWith: arrayUnion(recipient),
      ...(permission === "edit" ? { sharedEditors: arrayUnion(recipient) } : {})
    });

    await NotificationService.create(userId, "share", `File shared with ${recipient}`);
    await recordAdminAction("FILE_SHARE", {
      entityType: "file",
      entityId: fileId,
      newValue: { shareId: ref_.id, sharedWith: recipient, permission, expiryDays: expiryDays || null, watermark: data.watermark }
    });
    return { id: ref_.id, ...data };
  },

  async revokeShare(shareId) {
    const shareRef = doc(db, "shares", shareId);
    const snap = await getDoc(shareRef);
    await deleteDoc(shareRef);
    const share = snap.exists() ? snap.data() : null;
    if (share?.fileId && share?.sharedWith) {
      await updateDoc(doc(db, "files", share.fileId), {
        sharedWith: arrayRemove(share.sharedWith),
        sharedEditors: arrayRemove(share.sharedWith)
      });
    }
    await recordAdminAction("FILE_SHARE_REVOKE", {
      entityType: "file",
      entityId: share?.fileId || null,
      previousValue: share ? { shareId, sharedWith: share.sharedWith || null, permission: share.permission || null } : { shareId }
    });
  }
};




export const NotificationService = {
  async getNotifications(userId) {
    try {
      const q = query(collection(db, "notifications"), where("userId", "==", userId), orderBy("createdAt", "desc"), limit(50));
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (err) {
      if (err.message?.includes("requires an index")) {
        console.warn("Firestore index missing for notifications. Falling back to unordered query.", err.message);
        const q = query(collection(db, "notifications"), where("userId", "==", userId), limit(50));
        const snap = await getDocs(q);
        return snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
      }
      throw err;
    }
  },

  subscribeNotifications(userId, callback) {
    try {
      const q = query(collection(db, "notifications"), where("userId", "==", userId), orderBy("createdAt", "desc"), limit(50));
      return onSnapshot(q, (snap) => {
        callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      }, (err) => {
        if (err.message?.includes("requires an index")) {
          console.warn("Firestore index missing for notifications. Using fallback.", err.message);
          const fallbackQ = query(collection(db, "notifications"), where("userId", "==", userId), limit(50));
          onSnapshot(fallbackQ, (snap) => {
            const sorted = snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
            callback(sorted);
          });
        } else {
          console.error("Notification subscribe error:", err);
        }
      });
    } catch (err) {
      console.error("Notification subscribe setup error:", err);
      return () => {};
    }
  },

  async create(userId, type, message) {
    // Rules only accept notifications for another user when the sender is recorded
    // and the recipient is in the sender's account hierarchy (or a platform admin).
    await addDoc(collection(db, "notifications"), {
      userId, type, message, read: false, createdBy: currentUid(), createdAt: serverTimestamp()
    });
  },

  async markAllRead(userId) {
    const q = query(collection(db, "notifications"), where("userId", "==", userId), where("read", "==", false));
    const snap = await getDocs(q);
    const promises = snap.docs.map((d) => updateDoc(d.ref, { read: true }));
    await Promise.all(promises);
  }
};




export const UsageService = {
  async getUsage(userId) {
    const snap = await getDoc(doc(db, "usage", userId));
    if (!snap.exists()) return { operations: 0, operationLimit: 10000, storageUsed: 0, storageLimit: 50, concurrentUsers: 0, concurrentLimit: 20, aiTokens: 0 };
    return snap.data();
  },

  async incrementOperation(userId) {
    const usageRef = doc(db, "usage", userId);
    await setDoc(usageRef, { operations: increment(1) }, { merge: true });
    await GroupUsageService.record(getTenancy().tenantId, { ops: 1 }).catch(() => {});
  },

  /** Quota state if `bytes` more were stored (one operation); see services/quota. */
  async checkUpload(userId, bytes = 0) {
    const snap = await getDoc(doc(db, "usage", userId));
    const usage = readUsage(snap.exists() ? snap.data() : {});
    return { usage, ...evaluateQuota(usage, { bytes, ops: 1 }) };
  },

  /** Blocks when the user's own quota or the group total (4.4) would be exceeded. */
  async assertCanUpload(userId, bytes = 0) {
    if (isDemoMode()) return null;
    const result = await this.checkUpload(userId, bytes);
    if (result.blocked) throw new QuotaError(result.blocked, result.usage);
    const { tenantId } = getTenancy();
    const capacity = getGroupCapacity();
    if (tenantId && capacity) {
      const group = await GroupUsageService.check(tenantId, capacity, { bytes, ops: 1 });
      if (group.blocked) {
        const err = new QuotaError(group.blocked, group.usage);
        err.group = true;
        throw err;
      }
    }
    return result;
  },

  /** Counts one operation and the stored bytes (byte counter + legacy GB field). */
  async recordUpload(userId, bytes = 0) {
    if (isDemoMode()) return;
    const ref_ = doc(db, "usage", userId);
    const snap = await getDoc(ref_).catch(() => null);
    const before = readUsage(snap?.exists?.() ? snap.data() : {});
    await setDoc(ref_, {
      operations: increment(1),
      storageUsedBytes: increment(bytes),
      storageUsed: increment(bytes / GB),
      updatedAt: serverTimestamp()
    }, { merge: true });
    await GroupUsageService.record(getTenancy().tenantId, { ops: 1, bytes }).catch(() => {});
    const crossed = crossedWarning(before, { bytes, ops: 1 });
    if (crossed.length) {
      const after = evaluateQuota(before, { bytes, ops: 1 });
      await NotificationService.create(userId, "quota_warning",
        crossed.map((k) => k === "storage" ? `Storage ${after.storagePct}% used` : `Operations ${after.opsPct}% used`).join(" · ")
      ).catch(() => {});
    }
  }
};




/**
 * Records a privileged action under the signed-in actor. Best effort: the action
 * itself already happened, so a failed audit write is reported but not rethrown.
 */
export async function recordAdminAction(action, { entityType = null, entityId = null, previousValue = null, newValue = null, meta = {} } = {}) {
  const actor = currentUid();
  if (!actor || isDemoMode()) return;
  try {
    await AuditService.logChange({ userId: actor, action, entityType, entityId, previousValue, newValue, meta });
  } catch (err) {
    logError(`AuditService:${action}`, err);
  }
}

export const AuditService = {
  async getAuditLogs(userId) {
    const q = query(collection(db, "auditLogs"), where("userId", "==", userId), orderBy("createdAt", "desc"), limit(100));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  /** Legacy simple log — kept for existing callers */
  async log(userId, action, details = {}) {
    await addDoc(collection(db, "auditLogs"), {
      userId,
      action,
      details,
      entityType: details.entityType || null,
      entityId: details.entityId || null,
      previousValue: details.previousValue ?? null,
      newValue: details.newValue ?? null,
      createdAt: serverTimestamp()
    });
  },

  /**
   * Audit v2 — structured change log.
   * Fields: user, action, entity_type, entity_id, previous_value, new_value, timestamp (+ optional meta)
   */
  async logChange({
    userId,
    action,
    entityType = null,
    entityId = null,
    previousValue = null,
    newValue = null,
    meta = {},
    ip = null,
    requestId = null
  } = {}) {
    await addDoc(collection(db, "auditLogs"), {
      userId: userId || null,
      action: action || "UNKNOWN",
      entityType,
      entityId,
      previousValue: previousValue ?? null,
      newValue: newValue ?? null,
      details: { entityType, entityId, ...(meta || {}) },
      meta: meta || {},
      ip,
      requestId,
      createdAt: serverTimestamp()
    });
  },

  async listRecent({ limitCount = 100, entityType = null } = {}) {
    try {
      let qRef;
      if (entityType) {
        qRef = query(
          collection(db, "auditLogs"),
          where("entityType", "==", entityType),
          orderBy("createdAt", "desc"),
          limit(limitCount)
        );
      } else {
        qRef = query(collection(db, "auditLogs"), orderBy("createdAt", "desc"), limit(limitCount));
      }
      const snap = await getDocs(qRef);
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (err) {
      // Roles without audit access may only read their own entries.
      const rows = await listOwned("auditLogs", {
        filters: entityType ? [["entityType", entityType]] : [],
        includeDeleted: true
      });
      return rows.slice(0, limitCount);
    }
  }
};




export const WorkspaceService = {
  async getWorkspaces(userId) {
    const q = query(collection(db, "workspaces"), where("members", "array-contains", userId));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  /**
   * Every company has one default workspace (`default_<tenant uid>`); the
   * signed-in user creates it if missing and joins it if not yet a member.
   */
  async ensureDefaultWorkspace(profile, uid) {
    const tenant = tenantOf(profile, uid);
    const id = defaultWorkspaceId(tenant);
    if (!id) return null;
    const ref = doc(db, "workspaces", id);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      await setDoc(ref, {
        name: "Default", isDefault: true, tenantId: tenant, licenseCode: licenseCodeOf(profile),
        members: [uid], createdBy: uid, createdAt: serverTimestamp(), updatedAt: serverTimestamp()
      });
    } else if (!(snap.data().members || []).includes(uid)) {
      await updateDoc(ref, { members: arrayUnion(uid), updatedAt: serverTimestamp() });
    }
    return id;
  },

  /** Joins a company default workspace (e.g. a legal entity's) the user is not yet a member of. */
  async join(workspaceId, uid) {
    const ref = doc(db, "workspaces", workspaceId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error("WORKSPACE_NOT_FOUND");
    if (!(snap.data().members || []).includes(uid)) {
      await updateDoc(ref, { members: arrayUnion(uid), updatedAt: serverTimestamp() });
    }
    return { id: snap.id, ...snap.data() };
  }
};




export const LicenseService = {
  async lookupLicense(code) {
    const cleanCode = code.replace(/-/g, "").toUpperCase();
    const snap = await getDoc(doc(db, "licenses", cleanCode));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  },

  /** Licenses are only listable by Admins; other roles read them one code at a time. */
  async getLicensesByCodes(codes) {
    const ids = Array.from(new Set((codes || []).map((c) => String(c || "").trim().toUpperCase()).filter(Boolean)));
    // Older license documents are keyed by the dashed code, newer ones by the compact code.
    const snaps = await Promise.all(ids.map(async (id) => {
      const snap = await getDoc(doc(db, "licenses", id));
      const compact = id.replace(/-/g, "");
      if (snap.exists() || compact === id) return snap;
      return getDoc(doc(db, "licenses", compact));
    }));
    const byId = new Map();
    snaps.filter((s) => s.exists()).forEach((s) => byId.set(s.id, { id: s.id, ...s.data() }));
    return Array.from(byId.values());
  },

  /** Read-only: never writes to `licenses`. */
  async listAllLicenses() {
    if (isDemoMode()) return [];
    const snap = await getDocs(collection(db, "licenses"));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  /**
   * Copy licenses into `AdminLicenses` collection.
   * Does NOT update/delete/create documents in `licenses`.
   */
  async syncDrpackLicenses({ syncedBy = null } = {}) {
    const source = await this.listAllLicenses();
    const toPlain = (value) => {
      if (value == null) return null;
      if (typeof value?.toDate === "function") {
        try { return value.toDate().toISOString(); } catch (_) { return null; }
      }
      if (typeof value === "object" && typeof value.seconds === "number") {
        return new Date(value.seconds * 1000).toISOString();
      }
      return value;
    };
    const { normalizeCountryName } = await import("./countryNames");
    const writes = source.map((lic) => {
      const { id, ...data } = lic;
      const rawCountry = data.country || data.usageCountry || null;
      const soldTo = String(data.soldToCompany || "");
      const soldUpper = soldTo.toUpperCase();
      const inferredBrand = soldUpper.includes("GLOBE")
        ? "GLOBE"
        : (soldUpper.includes("DRPACK") ? "DRPACK" : (soldTo || "UNKNOWN"));
      const payload = {
        licenseCode: data.code || id,
        code: data.code || id,
        licenseName: data.licenseName || null,
        soldToCompany: data.soldToCompany || null,
        usedByCompany: data.usedByCompany || null,
        country: data.country || null,
        usageCountry: data.usageCountry || null,
        isUsed: !!data.isUsed,
        isActive: data.isActive !== false,
        status: data.status || null,
        validUntil: data.validUntil || null,
        verificationUrl: data.verificationUrl || null,
        createdAt: toPlain(data.createdAt) || data.createdAt || null,
        sourceLicenseId: id,
        sourceCollection: "licenses",
        brand: inferredBrand,
        assignedCountry: normalizeCountryName(rawCountry, null),
        usingCompany: data.usedByCompany || null,
        sellerCompany: data.soldToCompany || null,
        syncedAt: serverTimestamp(),
        syncedBy: syncedBy || null
      };
      return setDoc(doc(db, "AdminLicenses", id), payload, { merge: true });
    });
    await Promise.all(writes);
    await recordAdminAction("DRPACK_LICENSE_SYNC", {
      entityType: "AdminLicenses",
      newValue: { synced: source.length },
      meta: { syncedBy: syncedBy || null }
    });
    return { synced: source.length, licenses: source.map((l) => l.id) };
  },

  async listDrpackLicenses() {
    try {
      const snap = await getDocs(collection(db, "AdminLicenses"));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => {
        const ca = String(a.assignedCountry || a.country || a.usageCountry || "");
        const cb = String(b.assignedCountry || b.country || b.usageCountry || "");
        if (ca !== cb) return ca.localeCompare(cb, "tr");
        const ua = String(a.usingCompany || a.usedByCompany || "");
        const ub = String(b.usingCompany || b.usedByCompany || "");
        return ua.localeCompare(ub, "tr");
      });
      return list;
    } catch (err) {
      console.warn("LicenseService.listAdminLicenses:", err);
      return [];
    }
  }
};




export const LicenseLookupLogService = {
  async record(entry = {}) {
    try {
      const { shouldSkipBeforeContext, isExcludedIdentity } = await import("./logPrivacy");
      const user = auth?.currentUser;
      if (shouldSkipBeforeContext({ uid: user?.uid, email: user?.email })) return;
      const { collectClientLookupContext } = await import("./clientLookupContext");
      const ctx = await collectClientLookupContext();
      if (isExcludedIdentity({ ip: ctx.ip })) return;
      const payload = {};
      const raw = {
        licenseKey: String(entry.licenseKey || "").slice(0, 80),
        licenseKeyCompact: String(entry.licenseKey || "").replace(/-/g, "").slice(0, 64),
        resultStatus: String(entry.resultStatus || "unknown").slice(0, 40),
        licenseName: entry.licenseName || null,
        soldToCompany: entry.soldToCompany || null,
        usedByCompany: entry.usedByCompany || null,
        source: entry.source || "portal_auth_lookup",
        createdAt: serverTimestamp(),
        createdAtMs: Date.now(),
        clientRequestId: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`,
        ...ctx
      };
      Object.entries(raw).forEach(([k, v]) => {
        if (v !== undefined) payload[k] = v;
      });
      await addDoc(collection(db, "licenseLookupLogs"), payload);
    } catch (err) {
      console.warn("LicenseLookupLogService.record:", err);
    }
  },

  mapDocs(snap) {
    return (snap?.docs || []).map((d) => ({ id: d.id, ...d.data() }));
  },

  sortLogs(rows, limitCount = 300) {
    return [...rows].sort((a, b) => {
      const ta = a.createdAtMs || a.createdAt?.seconds * 1000 || a.createdAt?.toMillis?.() || 0;
      const tb = b.createdAtMs || b.createdAt?.seconds * 1000 || b.createdAt?.toMillis?.() || 0;
      return tb - ta;
    }).slice(0, limitCount);
  },

  async listRecent({ limitCount = 1000 } = {}) {
    if (isDemoMode()) return [];
    try {
      const qRef = query(
        collection(db, "licenseLookupLogs"),
        orderBy("createdAt", "desc"),
        limit(limitCount)
      );
      const snap = await getDocs(qRef);
      return this.mapDocs(snap);
    } catch (err) {
      console.warn("LicenseLookupLogService.listRecent:", err);
      const snap = await getDocs(collection(db, "licenseLookupLogs"));
      return this.sortLogs(this.mapDocs(snap), limitCount);
    }
  },

  subscribeRecent(callback, { limitCount = 1000 } = {}) {
    if (typeof callback !== "function") return () => {};
    if (isDemoMode()) {
      callback([]);
      return () => {};
    }
    const qRef = query(
      collection(db, "licenseLookupLogs"),
      orderBy("createdAt", "desc"),
      limit(limitCount)
    );
    return onSnapshot(
      qRef,
      (snap) => callback(this.mapDocs(snap)),
      async (err) => {
        console.warn("LicenseLookupLogService.subscribeRecent:", err);
        try {
          const snap = await getDocs(collection(db, "licenseLookupLogs"));
          callback(this.sortLogs(this.mapDocs(snap), limitCount), err);
        } catch (fallbackErr) {
          callback([], fallbackErr || err);
        }
      }
    );
  }
};




export const LoginLogService = {
  async record(entry = {}) {
    try {
      if (isDemoSession() || isDemoEmail(entry.email)) return;
      const uid = String(entry.uid || "").slice(0, 128);
      const email = String(entry.email || "").slice(0, 120);
      if (!uid || !email) return;
      const { shouldSkipBeforeContext, isExcludedIdentity } = await import("./logPrivacy");
      if (shouldSkipBeforeContext({ uid, email })) return;
      const { collectClientLookupContext } = await import("./clientLookupContext");
      const ctx = await collectClientLookupContext();
      if (isExcludedIdentity({ ip: ctx.ip })) return;
      const payload = {};
      const raw = {
        uid,
        email,
        displayName: entry.displayName ? String(entry.displayName).slice(0, 120) : null,
        source: String(entry.source || "portal_web").slice(0, 40),
        createdAt: serverTimestamp(),
        createdAtMs: Date.now(),
        clientRequestId: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`,
        ...ctx
      };
      Object.entries(raw).forEach(([k, v]) => {
        if (v !== undefined) payload[k] = v;
      });
      await addDoc(collection(db, "loginLogs"), payload);
    } catch (err) {
      console.warn("LoginLogService.record:", err);
    }
  },

  mapDocs(snap) {
    return (snap?.docs || []).map((d) => ({ id: d.id, ...d.data() }));
  },

  sortLogs(rows, limitCount = 300) {
    return [...rows].sort((a, b) => {
      const ta = a.createdAtMs || a.createdAt?.seconds * 1000 || a.createdAt?.toMillis?.() || 0;
      const tb = b.createdAtMs || b.createdAt?.seconds * 1000 || b.createdAt?.toMillis?.() || 0;
      return tb - ta;
    }).slice(0, limitCount);
  },

  /** The signed-in user's own sign-ins, newest first. */
  async listMine({ limitCount = 20 } = {}) {
    const uid = currentUid();
    if (!uid || isDemoSession()) return [];
    const snap = await getDocs(query(collection(db, "loginLogs"), where("uid", "==", uid), limit(200)));
    return this.sortLogs(this.mapDocs(snap), limitCount);
  },

  async listRecent({ limitCount = 1000 } = {}) {
    if (isDemoSession()) return [];
    try {
      const qRef = query(
        collection(db, "loginLogs"),
        orderBy("createdAt", "desc"),
        limit(limitCount)
      );
      const snap = await getDocs(qRef);
      return this.mapDocs(snap);
    } catch (err) {
      console.warn("LoginLogService.listRecent:", err);
      const snap = await getDocs(collection(db, "loginLogs"));
      return this.sortLogs(this.mapDocs(snap), limitCount);
    }
  },

  subscribeRecent(callback, { limitCount = 1000 } = {}) {
    if (typeof callback !== "function") return () => {};
    if (isDemoSession()) {
      callback([]);
      return () => {};
    }
    const qRef = query(
      collection(db, "loginLogs"),
      orderBy("createdAt", "desc"),
      limit(limitCount)
    );
    return onSnapshot(
      qRef,
      (snap) => callback(this.mapDocs(snap)),
      async (err) => {
        console.warn("LoginLogService.subscribeRecent:", err);
        try {
          const snap = await getDocs(collection(db, "loginLogs"));
          callback(this.sortLogs(this.mapDocs(snap), limitCount), err);
        } catch (fallbackErr) {
          callback([], fallbackErr || err);
        }
      }
    );
  }
};




export const RoleService = {
  async ensureRolesSeeded() {
    if (isDemoMode()) return 0;
    const {
      ROLE_DEFINITIONS,
      ROLE_ORDER
    } = await import("./roles");
    const writes = [];
    for (const roleId of ROLE_ORDER) {
      const def = ROLE_DEFINITIONS[roleId];
      writes.push(
        setDoc(
          doc(db, "roles", roleId),
          {
            ...def,
            updatedAt: serverTimestamp()
          },
          { merge: true }
        )
      );
    }
    await Promise.all(writes);
    return ROLE_ORDER.length;
  },

  async getRoles() {
    const { ROLE_DEFINITIONS, ROLE_ORDER } = await import("./roles");
    if (isDemoMode()) {
      return ROLE_ORDER.map((id) => ({ ...ROLE_DEFINITIONS[id] }));
    }
    try {
      const snap = await getDocs(collection(db, "roles"));
      if (snap.empty) {
        return ROLE_ORDER.map((id) => ({ ...ROLE_DEFINITIONS[id] }));
      }
      const byId = {};
      snap.docs.forEach((d) => {
        byId[d.id] = { id: d.id, ...d.data() };
      });
      return ROLE_ORDER.map((id) => byId[id] || { ...ROLE_DEFINITIONS[id] });
    } catch (err) {
      console.warn("RoleService.getRoles fallback:", err);
      return ROLE_ORDER.map((id) => ({ ...ROLE_DEFINITIONS[id] }));
    }
  },

  async updateRolePermissions(roleId, permissions) {
    if (isDemoMode()) return;
    const before = await getDoc(doc(db, "roles", roleId)).catch(() => null);
    await setDoc(
      doc(db, "roles", roleId),
      { permissions, updatedAt: serverTimestamp() },
      { merge: true }
    );
    await recordAdminAction("ROLE_PERMISSIONS_UPDATE", {
      entityType: "role",
      entityId: roleId,
      previousValue: before?.exists?.() ? before.data().permissions ?? null : null,
      newValue: permissions
    });
  }
};




export const UserService = {
  async getUserProfile(uid) {
    if (!uid) return null;
    const snap = await getDoc(doc(db, "users", uid));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  },

  async ensureUserProfile(firebaseUser, defaults = {}) {
    const {
      normalizeRole,
      isFullAdminEmail,
      ROLES,
      getPermissionsForRole,
      getDemoBrowsePermissions
    } = await import("./roles");

    if (!firebaseUser?.uid) return null;

    const email = firebaseUser.email || defaults.email || "";
    if (isDemoEmail(email)) {
      return {
        id: firebaseUser.uid,
        email,
        displayName: firebaseUser.displayName || defaults.displayName || "Demo User",
        role: ROLES.USER,
        permissions: getDemoBrowsePermissions()
      };
    }

    const userRef = doc(db, "users", firebaseUser.uid);
    const snap = await getDoc(userRef);
    const forceFullAdmin = isFullAdminEmail(email);

    let role = forceFullAdmin
      ? ROLES.FULL_ADMIN
      : normalizeRole(defaults.role || (snap.exists() ? snap.data().role : ROLES.USER));

    if (forceFullAdmin) role = ROLES.FULL_ADMIN;

    const base = {
      email,
      displayName: firebaseUser.displayName || defaults.displayName || email.split("@")[0] || "User",
      role,
      parentUid: defaults.parentUid ?? (snap.exists() ? snap.data().parentUid : null) ?? null,
      organizationId: defaults.organizationId ?? (snap.exists() ? snap.data().organizationId : null) ?? null,
      updatedAt: serverTimestamp()
    };

    if (!snap.exists()) {
      await setDoc(userRef, {
        ...base,
        createdAt: serverTimestamp(),
        licenseCode: defaults.licenseCode || null
      });
    } else {
      const patch = { updatedAt: serverTimestamp() };
      if (forceFullAdmin && snap.data().role !== ROLES.FULL_ADMIN) {
        patch.role = ROLES.FULL_ADMIN;
      }
      if (!snap.data().email && email) patch.email = email;
      if (!snap.data().displayName && base.displayName) patch.displayName = base.displayName;
      await setDoc(userRef, patch, { merge: true });
    }

    const profile = await this.getUserProfile(firebaseUser.uid);
    if (isDemoEmail(email)) {
      return {
        ...profile,
        email,
        role: ROLES.USER,
        permissions: getDemoBrowsePermissions()
      };
    }
    const permissions = getPermissionsForRole(profile?.role);
    return { ...profile, permissions };
  },

  async listUsers() {
    try {
      const snap = await getDocs(collection(db, "users"));
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (err) {
      // Company managers may only read their own profile and the users linked
      // to them, so the full-collection read is denied and must be scoped.
      const me = currentUid();
      const own = await this.getUserProfile(me);
      const byId = new Map(own ? [[own.id, own]] : []);
      for (const field of ["parentUid", "createdBy"]) {
        try {
          const linked = await getDocs(query(collection(db, "users"), where(field, "==", me)));
          linked.docs.forEach((d) => byId.set(d.id, { id: d.id, ...d.data() }));
        } catch (_) { /* role without user-management rights */ }
      }
      return Array.from(byId.values());
    }
  },

  async updateUserRole(uid, role, meta = {}) {
    const { normalizeRole, ROLES } = await import("./roles");
    const {
      normalizeCompanyInfo,
      validateAdminCompanyInfo,
      validateSoldCompanyInfo
    } = await import("./companyProfile");
    const cleanRole = normalizeRole(role);
    const previous = await this.getUserProfile(uid).catch(() => null);

    let companyInfo = undefined;
    if (meta.companyInfo !== undefined) {
      companyInfo = normalizeCompanyInfo(meta.companyInfo);
    }

    if (cleanRole === ROLES.ADMIN) {
      const info = companyInfo !== undefined
        ? companyInfo
        : normalizeCompanyInfo(previous?.companyInfo);
      const err = validateAdminCompanyInfo(info);
      if (err) throw new Error(err);
      companyInfo = info;
    } else if (cleanRole === ROLES.COMPANY && companyInfo !== undefined) {
      const err = validateSoldCompanyInfo(companyInfo);
      if (err) throw new Error(err);
    }

    const payload = {
      role: cleanRole,
      updatedAt: serverTimestamp()
    };
    if (meta.parentUid !== undefined) payload.parentUid = meta.parentUid;
    if (meta.organizationId !== undefined) payload.organizationId = meta.organizationId;
    if (meta.displayName !== undefined) payload.displayName = meta.displayName;
    if (companyInfo !== undefined) {
      payload.companyInfo = companyInfo;
      payload.isCompanyAccount = cleanRole === ROLES.ADMIN;
    } else if (cleanRole !== ROLES.ADMIN) {
      payload.isCompanyAccount = false;
    }

    await setDoc(doc(db, "users", uid), payload, { merge: true });

    if (cleanRole === ROLES.ADMIN && companyInfo) {
      await setDoc(doc(db, "companies", uid), {
        adminUserId: uid,
        ...companyInfo,
        updatedAt: serverTimestamp()
      }, { merge: true });
    }

    await recordAdminAction("ROLE_CHANGE", {
      entityType: "user",
      entityId: uid,
      previousValue: previous ? { role: previous.role || null, parentUid: previous.parentUid ?? null, organizationId: previous.organizationId ?? null } : null,
      newValue: { role: cleanRole, parentUid: payload.parentUid ?? previous?.parentUid ?? null, organizationId: payload.organizationId ?? previous?.organizationId ?? null },
      meta: { targetEmail: previous?.email || null, tradeName: companyInfo?.tradeName || null }
    });
  },

  /** Deactivate / reactivate an account; the rules then deny the user all data access. */
  async setUserDisabled(uid, disabled) {
    const actor = currentUid();
    if (!uid || uid === actor) throw new Error("CANNOT_DISABLE_SELF");
    await setDoc(doc(db, "users", uid), {
      disabled: !!disabled,
      disabledAt: disabled ? serverTimestamp() : null,
      disabledBy: disabled ? actor : null,
      updatedAt: serverTimestamp()
    }, { merge: true });
    await recordAdminAction(disabled ? "USER_DISABLE" : "USER_ENABLE", {
      entityType: "user",
      entityId: uid,
      previousValue: { disabled: !disabled },
      newValue: { disabled: !!disabled }
    });
  },

  /**
   * Create Auth + Firestore user without switching the signed-in admin session.
   * Uses a secondary Firebase Auth app instance.
   * Admin role requires companyInfo (trade name, tax id, address, etc.).
   */
  async createManagedUser({
    email,
    password,
    displayName,
    role,
    parentUid = null,
    organizationId = null,
    createdBy = null,
    companyInfo = null
  }) {
    const {
      normalizeRole,
      ROLES,
      isFullAdminEmail,
      CREATABLE_ROLES
    } = await import("./roles");
    const {
      normalizeCompanyInfo,
      validateAdminCompanyInfo,
      validateSoldCompanyInfo
    } = await import("./companyProfile");
    const {
      createUserWithEmailAndPassword,
      updateProfile,
      signOut
    } = await import("firebase/auth");
    const { getSecondaryAuth } = await import("../config/firebase");

    const cleanEmail = String(email || "").trim().toLowerCase();
    const cleanRole = normalizeRole(role);

    if (!cleanEmail || !/\S+@\S+\.\S+/.test(cleanEmail)) {
      throw new Error("INVALID_EMAIL");
    }
    if (!password || String(password).length < 6) {
      throw new Error("WEAK_PASSWORD");
    }
    if (!CREATABLE_ROLES.includes(cleanRole) || cleanRole === ROLES.FULL_ADMIN) {
      throw new Error("CANNOT_CREATE_ROLE");
    }
    if (isFullAdminEmail(cleanEmail)) {
      throw new Error("EMAIL_RESERVED");
    }

    let normalizedCompany = null;
    if (cleanRole === ROLES.ADMIN) {
      normalizedCompany = normalizeCompanyInfo(companyInfo || {});
      const companyErr = validateAdminCompanyInfo(normalizedCompany);
      if (companyErr) throw new Error(companyErr);
    } else if (cleanRole === ROLES.COMPANY && companyInfo) {
      normalizedCompany = normalizeCompanyInfo(companyInfo);
      const companyErr = validateSoldCompanyInfo(normalizedCompany);
      if (companyErr) throw new Error(companyErr);
    }

    const existing = await this.findUserByEmail(cleanEmail);
    if (existing) {
      throw new Error("EMAIL_IN_USE");
    }

    // Group user quota (4.4): every active license of the company adds seats.
    if (parentUid && cleanRole !== ROLES.ADMIN) {
      const users = await this.listUsers();
      const parent = users.find((u) => u.id === parentUid) || await this.getUserProfile(parentUid);
      const codes = [parent?.licenseCode, ...(parent?.licenseCodes || [])].filter(Boolean);
      const licenses = codes.length ? await LicenseService.getLicensesByCodes(codes) : [];
      assertUserCapacity(countGroupUsers(users, parentUid), groupCapacity(licenses));
    }

    const secondaryAuth = getSecondaryAuth();
    try {
      const cred = await createUserWithEmailAndPassword(secondaryAuth, cleanEmail, password);
      const name = (displayName || normalizedCompany?.tradeName || "").trim() || cleanEmail.split("@")[0];
      if (name) {
        await updateProfile(cred.user, { displayName: name });
      }

      const userPayload = {
        email: cleanEmail,
        displayName: name,
        role: cleanRole,
        parentUid: parentUid || null,
        organizationId: organizationId || null,
        createdBy: createdBy || null,
        isCompanyAccount: cleanRole === ROLES.ADMIN,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      if (normalizedCompany) {
        userPayload.companyInfo = normalizedCompany;
      }

      await setDoc(doc(db, "users", cred.user.uid), userPayload);

      if (cleanRole === ROLES.ADMIN && normalizedCompany) {
        await setDoc(doc(db, "companies", cred.user.uid), {
          adminUserId: cred.user.uid,
          ...normalizedCompany,
          createdBy: createdBy || null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }

      await signOut(secondaryAuth);

      await recordAdminAction("USER_CREATE", {
        entityType: "user",
        entityId: cred.user.uid,
        newValue: { email: cleanEmail, role: cleanRole, parentUid: parentUid || null, organizationId: organizationId || null }
      });

      return {
        id: cred.user.uid,
        email: cleanEmail,
        displayName: name,
        role: cleanRole,
        parentUid: parentUid || null,
        companyInfo: normalizedCompany
      };
    } catch (err) {
      try {
        await signOut(secondaryAuth);
      } catch (_) { /* ignore */ }
      if (err?.code === "auth/email-already-in-use") {
        throw new Error("EMAIL_IN_USE");
      }
      if (err?.code === "auth/weak-password") {
        throw new Error("WEAK_PASSWORD");
      }
      if (err?.code === "auth/invalid-email") {
        throw new Error("INVALID_EMAIL");
      }
      throw err;
    }
  },

  async findUserByEmail(email) {
    const clean = String(email || "").trim().toLowerCase();
    if (!clean) return null;
    try {
      const q = query(collection(db, "users"), where("email", "==", clean));
      const snap = await getDocs(q);
      if (snap.empty) {
        // fallback: case-sensitive / mixed case scan
        const all = await getDocs(collection(db, "users"));
        const match = all.docs.find(
          (d) => String(d.data().email || "").toLowerCase() === clean
        );
        return match ? { id: match.id, ...match.data() } : null;
      }
      const d = snap.docs[0];
      return { id: d.id, ...d.data() };
    } catch (err) {
      console.warn("UserService.findUserByEmail:", err);
      return null;
    }
  }
};




export const InvoiceService = {
  async getInvoices(userId) {
    return listOwned("invoices", { uid: userId || currentUid() });
  },

  /** Totals over every invoice of the user, computed on the server (the list itself is paged). */
  async getInvoiceTotals(userId) {
    const uid = userId || currentUid();
    const base = [scopeWhere("invoices", ownerField("invoices"), uid, currentUid())];
    const coll = collection(db, "invoices");
    const legacy = async () => {
      const all = await listOwned("invoices", { uid });
      const total = (rows) => rows.reduce((acc, i) => acc + (Number(i.totalAmount) || 0), 0);
      return {
        count: all.length,
        total: total(all),
        paid: total(all.filter((i) => i.status === "paid")),
        pending: total(all.filter((i) => i.status === "pending" || i.status === "approved"))
      };
    };
    try {
      const [all, paid, pending] = await Promise.all([
      getAggregateFromServer(query(coll, ...base), { count: count(), total: sum("totalAmount") }),
      getAggregateFromServer(query(coll, ...base, where("status", "==", "paid")), { total: sum("totalAmount") }),
      getAggregateFromServer(query(coll, ...base, where("status", "in", ["pending", "approved"])), { total: sum("totalAmount") })]
      );
      return {
        count: all.data().count,
        total: all.data().total || 0,
        paid: paid.data().total || 0,
        pending: pending.data().total || 0
      };
    } catch (err) {
      logError("InvoiceService.getInvoiceTotals", err);
      return legacy();
    }
  },

  getInvoicesPage(cursor = null, { status = null } = {}) {
    return listOwnedPage("invoices", { cursor, filters: status ? [["status", status]] : [] });
  },

  async getInvoice(invoiceId) {
    const snap = await getDoc(doc(db, "invoices", invoiceId));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  },

  /** Throws Error(code) when the kind rules (credit cap, advance balance, progress %) are broken. */
  async validateKind(data) {
    const kind = data.invoiceKind || "standard";
    if (kind === "standard" && !(data.advanceDeductions || []).length && !data.language) return;
    const err = validateInvoiceKind(data, await this.getInvoices());
    if (err) throw Object.assign(new Error(err), { code: err });
  },

  async createInvoice(data) {
    await this.validateKind(data);
    const docRef = await addDoc(collection(db, "invoices"), {
      ...data,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return docRef.id;
  },

  async updateInvoice(invoiceId, data) {
    await updateDoc(doc(db, "invoices", invoiceId), {
      ...data,
      updatedAt: serverTimestamp()
    });
  },

  async deleteInvoice(invoiceId) {
    const referenced = (await this.getInvoices()).some((i) => i.id !== invoiceId && i.status !== "cancelled"
      && (i.originalInvoiceId === invoiceId || (i.advanceDeductions || []).some((d) => d.invoiceId === invoiceId)));
    if (referenced) throw Object.assign(new Error("INVOICE_REFERENCED"), { code: "INVOICE_REFERENCED" });
    await deleteDoc(doc(db, "invoices", invoiceId));
  },

  async updateStatus(invoiceId, status) {
    const update = { status, updatedAt: serverTimestamp() };
    if (status === "paid") update.paidDate = serverTimestamp();
    await updateDoc(doc(db, "invoices", invoiceId), update);
  }
};




export const AccountService = {
  async getAccounts(userId) {
    return listOwned("accounts", { uid: userId || currentUid() });
  },

  /** Balances and counts over every account, computed on the server (the list itself is paged). */
  async getAccountTotals(userId) {
    const uid = userId || currentUid();
    const coll = collection(db, "accounts");
    const base = [scopeWhere("accounts", ownerField("accounts"), uid, currentUid())];
    const agg = (extra, spec) => getAggregateFromServer(query(coll, ...base, ...extra), spec);
    try {
      const [all, receivable, payable, customers, vendors] = await Promise.all([
      agg([], { count: count() }),
      agg([where("balance", ">", 0)], { total: sum("balance") }),
      agg([where("balance", "<", 0)], { total: sum("balance") }),
      agg([where("type", "==", "customer")], { count: count() }),
      agg([where("type", "==", "vendor")], { count: count() })]
      );
      return {
        count: all.data().count,
        totalReceivable: receivable.data().total || 0,
        totalPayable: Math.abs(payable.data().total || 0),
        customers: customers.data().count,
        vendors: vendors.data().count
      };
    } catch (err) {
      logError("AccountService.getAccountTotals", err);
      const rows = await listOwned("accounts", { uid });
      return {
        count: rows.length,
        totalReceivable: rows.filter((a) => (a.balance || 0) > 0).reduce((acc, a) => acc + a.balance, 0),
        totalPayable: rows.filter((a) => (a.balance || 0) < 0).reduce((acc, a) => acc + Math.abs(a.balance), 0),
        customers: rows.filter((a) => a.type === "customer").length,
        vendors: rows.filter((a) => a.type === "vendor").length
      };
    }
  },

  getAccountsPage(cursor = null) {
    return listOwnedPage("accounts", { cursor });
  },

  async getAccount(accountId) {
    const snap = await getDoc(doc(db, "accounts", accountId));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  },

  async createAccount(data) {
    const docRef = await addDoc(collection(db, "accounts"), {
      ...data,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return docRef.id;
  },

  async updateAccount(accountId, data) {
    await updateDoc(doc(db, "accounts", accountId), {
      ...data,
      updatedAt: serverTimestamp()
    });
  },

  async deleteAccount(accountId) {
    await deleteDoc(doc(db, "accounts", accountId));
  }
};




export const TransactionService = {
  async getTransactions(accountId) {
    return listOwned("transactions", { filters: [["accountId", accountId]], sort: "date", includeDeleted: true });
  },

  async createTransaction(data) {
    const docRef = await addDoc(collection(db, "transactions"), {
      ...data,
      createdAt: serverTimestamp()
    });
    return docRef.id;
  },

  async deleteTransaction(transactionId) {
    await deleteDoc(doc(db, "transactions", transactionId));
  }
};


export const ApprovalService = {
  async getApprovals(userId) {
    const uid = userId || currentUid();
    if (!uid) return [];
    const lists = await Promise.all(
      ["requestedBy", "createdBy", "currentApproverId"].map(async (field) => {
        const snap = await getDocs(query(collection(db, "approvalRequests"), where(field, "==", uid)));
        return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      })
    );
    const byId = new Map();
    lists.flat().forEach((row) => byId.set(row.id, row));
    return sortRows([...byId.values()].filter((r) => !r.deletedAt));
  },

  async getApproval(approvalId) {
    const snap = await getDoc(doc(db, "approvalRequests", approvalId));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  },

  async createApproval(data) {
    const docRef = await addDoc(collection(db, "approvalRequests"), {
      ...data,
      createdAt: serverTimestamp()
    });
    return docRef.id;
  },

  async updateApproval(approvalId, data) {
    await updateDoc(doc(db, "approvalRequests", approvalId), {
      ...data,
      updatedAt: serverTimestamp()
    });
  },

  async deleteApproval(approvalId) {
    await deleteDoc(doc(db, "approvalRequests", approvalId));
  }
};




export const ProductService = {
  async getProducts(userId) {
    return listOwned("products", { uid: userId || currentUid() });
  },

  async getProduct(productId) {
    const snap = await getDoc(doc(db, "products", productId));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  },

  async createProduct(data) {
    const docRef = await addDoc(collection(db, "products"), {
      ...data,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return docRef.id;
  },

  async updateProduct(productId, data) {
    await updateDoc(doc(db, "products", productId), {
      ...data,
      updatedAt: serverTimestamp()
    });
  },

  async deleteProduct(productId) {
    await deleteDoc(doc(db, "products", productId));
  },

  async getStockMovements(productId) {
    return listOwned("stockMovements", { filters: [["productId", productId]], includeDeleted: true });
  },

  async addStockMovement(productId, data) {
    const docRef = await addDoc(collection(db, "stockMovements"), {
      ...data,
      productId,
      createdAt: serverTimestamp()
    });
    return docRef.id;
  },

  async uploadImages(files, sku) {
    const urls = [];
    for (const file of files) {
      const fileRef = ref(storage, `products/${sku}/${Date.now()}-${file.name}`);
      const uploadTask = uploadBytesResumable(fileRef, file);
      await new Promise((resolve, reject) => {
        uploadTask.on("state_changed", null, reject, resolve);
      });
      const url = await getDownloadURL(fileRef);
      urls.push(url);
    }
    return urls;
  }
};




export const StockMovementService = {
  async getAllMovements() {
    return listOwned("stockMovements");
  },

  getMovementsPage(cursor = null, { productId = null } = {}) {
    return listOwnedPage("stockMovements", { cursor, filters: productId ? [["productId", productId]] : [] });
  },

  /** In/out quantities and transfer/adjustment counts over every movement, computed on the server. */
  async getMovementTotals() {
    const uid = currentUid();
    const coll = collection(db, "stockMovements");
    const byType = (type, spec) =>
    getAggregateFromServer(query(coll, where(ownerField("stockMovements"), "==", uid), where("type", "==", type)), spec);
    try {
      const [inn, out, transfers, adjustments] = await Promise.all([
      byType("in", { qty: sum("quantity") }),
      byType("out", { qty: sum("quantity") }),
      byType("transfer", { n: count() }),
      byType("adjustment", { n: count() })]
      );
      return {
        totalIn: inn.data().qty || 0,
        totalOut: out.data().qty || 0,
        transfers: transfers.data().n,
        adjustments: adjustments.data().n
      };
    } catch (err) {
      logError("StockMovementService.getMovementTotals", err);
      const rows = await listOwned("stockMovements");
      const qty = (type) => rows.filter((m) => m.type === type).reduce((acc, m) => acc + (m.quantity || 0), 0);
      return {
        totalIn: qty("in"),
        totalOut: qty("out"),
        transfers: rows.filter((m) => m.type === "transfer").length,
        adjustments: rows.filter((m) => m.type === "adjustment").length
      };
    }
  },

  async getMovementsByProduct(productId) {
    return listOwned("stockMovements", { filters: [["productId", productId]], includeDeleted: true });
  },

  async createMovementWithStockUpdate(data) {
    // Delegate to Phase 3 inventory engine (supports transfer + warehouse balances)
    const { InventoryTransactionService } = await import("./inventoryService");
    return InventoryTransactionService.post(data);
  }
};




export const ShipmentDocService = {
  async getShipmentDocs() {
    return listOwned("shipmentDocs");
  },

  async getShipmentDoc(id) {
    const snap = await getDoc(doc(db, "shipmentDocs", id));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  },

  async createShipmentDoc(data) {
    const docRef = await addDoc(collection(db, "shipmentDocs"), {
      ...data,
      createdAt: serverTimestamp()
    });
    return { id: docRef.id };
  },

  async uploadShipmentFile(file, docNo) {
    const fileRef = ref(storage, `shipmentDocs/${docNo}/${Date.now()}-${file.name}`);
    const uploadTask = uploadBytesResumable(fileRef, file);
    await new Promise((resolve, reject) => {
      uploadTask.on("state_changed", null, reject, resolve);
    });
    return getDownloadURL(fileRef);
  }
};




export const PurchaseRequestService = {
  async getRequests() {
    return listOwned("purchaseRequests");
  },

  async getRequest(id) {
    const snap = await getDoc(doc(db, "purchaseRequests", id));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  },

  async createRequest(data) {
    const docRef = await addDoc(collection(db, "purchaseRequests"), {
      ...data,
      createdAt: serverTimestamp()
    });
    return { id: docRef.id };
  },

  async updateStatus(id, status, extra = {}) {
    const ref = doc(db, "purchaseRequests", id);
    const snap = await getDoc(ref);
    const previousStatus = snap.exists() ? snap.data().status : null;
    await updateDoc(ref, {
      status,
      ...extra,
      updatedAt: serverTimestamp()
    });
    try {
      const { StatusHistoryService } = await import("./statusHistoryService");
      await StatusHistoryService.append({
        entityType: "purchaseRequests",
        entityId: id,
        previousStatus,
        newStatus: status,
        userId: extra.updatedBy || extra.performedBy || null,
        note: extra.note || null
      });
    } catch (err) {
      console.warn("status history append failed:", err);
    }
  },

  async deleteRequest(id) {
    await deleteDoc(doc(db, "purchaseRequests", id));
  }
};


export const SerialNumberService = {
  async getByProduct(productId) {
    return listOwned("serialNumbers", { filters: [["productId", productId]], includeDeleted: true });
  },

  async getById(id) {
    const snap = await getDoc(doc(db, "serialNumbers", id));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  },

  async create(data) {
    const docRef = await addDoc(collection(db, "serialNumbers"), {
      ...data,
      createdAt: serverTimestamp()
    });
    return { id: docRef.id };
  },

  async updateStatus(id, status, note = "") {
    const ref = doc(db, "serialNumbers", id);
    const snap = await getDoc(ref);
    const prev = snap.data();
    const history = prev.movementHistory || [];
    history.push({ date: new Date(), type: status, note });
    await updateDoc(ref, {
      status,
      movementHistory: history,
      updatedAt: serverTimestamp()
    });
  },

  async update(id, data) {
    await updateDoc(doc(db, "serialNumbers", id), {
      ...data,
      updatedAt: serverTimestamp()
    });
  }
};


export const LotService = {
  async getByProduct(productId) {
    return listOwned("lots", { filters: [["productId", productId]], includeDeleted: true });
  },

  async getById(id) {
    const snap = await getDoc(doc(db, "lots", id));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  },

  async create(data) {
    const docRef = await addDoc(collection(db, "lots"), {
      ...data,
      createdAt: serverTimestamp()
    });
    return { id: docRef.id };
  },

  async updateStatus(id, status) {
    await updateDoc(doc(db, "lots", id), {
      status,
      updatedAt: serverTimestamp()
    });
  },

  async update(id, data) {
    await updateDoc(doc(db, "lots", id), {
      ...data,
      updatedAt: serverTimestamp()
    });
  }
};
